// src/components/admin/adminSlidemenu.tsx - SIMPLE ROLE-BASED ACCESS
// Super Admin: Full access to everything
// Admin: Full access except Admin Users section (role_master, role_mapping, register_admin)
// Operation: Only Order Management (full) + Product Management (read-only) + Dashboard (read-only)
// Operation users are auto-redirected to Product Management page on login

import React, { useState, useEffect } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import AddCircleOutlineTwoToneIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarsIcon from '@mui/icons-material/Stars';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import logo from '../../assets/VOTOlogo.png';

interface SlideMenuProps {
  mobileOpen: boolean;
  onToggle: () => void;
}

const SlideMenu: React.FC<SlideMenuProps> = ({ mobileOpen, onToggle }) => {
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showAdminUsersMenu, setShowAdminUsersMenu] = useState(false);
  const [adminPermissions, setAdminPermissions] = useState<any>({});
  const location = useLocation();
  const navigate = useNavigate();
  
  const userRole = (localStorage.getItem('admin_type') || localStorage.getItem('user_role') || 'operation').toLowerCase().trim();
  const isSuperAdmin = userRole.includes('super');
  const isAdmin = userRole === 'admin';
  const isOperation = userRole === 'operation';

  // Auto-redirect Operation users from Dashboard to Product Management
  useEffect(() => {
    if (isOperation && location.pathname === '/adminDashboard') {
      console.log('🔄 Redirecting Operation user from Dashboard to Product Management');
      navigate('/productManagement', { replace: true });
    }
  }, [isOperation, location.pathname, navigate]);

  useEffect(() => {
    console.log('👤 Current user role:', userRole);
    
    // Check for custom role mappings in localStorage
    const roleMappingsStr = localStorage.getItem('role_mappings');
    if (roleMappingsStr) {
      try {
        const roleMappings = JSON.parse(roleMappingsStr);
        const currentMapping = roleMappings.find((m: any) => 
          m.admin_type?.toLowerCase().trim() === userRole
        );
        
        if (currentMapping) {
          console.log('✅ Found custom role mapping:', currentMapping);
          const rights = (currentMapping.rights || '').split(',').map((r: string) => r.trim().toLowerCase());
          const modules = (currentMapping.module_or_screen || '').split(',').map((m: string) => m.trim().toLowerCase());
          
          const customPermissions: any = {};
          modules.forEach((moduleId: string) => {
            if (moduleId === 'all') {
              // Grant all permissions
              getMenuItems().forEach(item => {
                customPermissions[item.id] = {
                  hasAccess: true,
                  canWrite: rights.some(r => ['add', 'edit', 'delete', 'write'].includes(r)),
                };
              });
            } else {
              customPermissions[moduleId] = {
                hasAccess: true,
                canWrite: rights.some(r => ['add', 'edit', 'delete', 'write'].includes(r)),
              };
            }
          });
          
          setAdminPermissions(customPermissions);
          console.log('📋 Custom permissions loaded:', customPermissions);
          return;
        }
      } catch (error) {
        console.error('❌ Error parsing role mappings:', error);
      }
    }
    
    // Use default permissions if no custom mapping found
    console.log('📋 Using default role permissions');
    setAdminPermissions({});
  }, [userRole]);

  const getMenuItems = () => [
    { id: 'dashboard', name: 'Dashboard', path: '/adminDashboard', icon: <DashboardCustomizeOutlinedIcon fontSize="small" /> },
    { id: 'user_list', name: 'User List', path: '/Userlist', icon: <GroupIcon fontSize="small" /> },
    { id: 'product_management', name: 'Manage product list', path: '/productManagement', icon: <InventoryIcon fontSize="small" /> },
    { id: 'category_management', name: 'Category Management', path: '/categoryManagement', icon: <CategoryOutlinedIcon fontSize="small" /> },
    { id: 'unit_management', name: 'Unit Management', path: '/unitManagement', icon: <GavelIcon fontSize="small" /> },
    { id: 'order_management', name: 'Order Management', path: '/adminOrderManagement', icon: <AddCircleOutlineTwoToneIcon fontSize="small" /> },
    { id: 'warranty_management', name: 'Warranty Management', path: '/WarrantyManagement', icon: <VerifiedUserIcon fontSize="small" /> },
    { id: 'subcategory_management', name: 'Sub Category Management', path: '/subcategorymanagement', icon: <CategoryOutlinedIcon fontSize="small" /> },
    { id: 'delivery_agent_management', name: 'Delivery Agent Management', path: '/DeliveryAgentManagement', icon: <CategoryOutlinedIcon fontSize="small" /> },
    { id: 'warehouse_management', name: 'Ware house Management', path: '/WarehouseManagement', icon: <WarehouseIcon fontSize="small" /> },
    { id: 'banner_management', name: 'Banner Management', path: '/banner', icon: <ViewCarouselOutlinedIcon fontSize="small" /> },
    { id: 'subbanner_management', name: 'Sub Banner Management', path: '/subbanner', icon: <ViewCarouselOutlinedIcon fontSize="small" /> },
    { id: 'review', name: 'Review', path: '/review', icon: <ViewCarouselOutlinedIcon fontSize="small" /> },
    { id: 'guest_order_list', name: 'Guest User Details', path: '/guestOrderlist', icon: <AccountCircleIcon fontSize="small" /> },
    { id: 'guest_user_order', name: 'Guest Users Order List', path: '/guestUserOrder', icon: <AccountCircleIcon fontSize="small" /> },
    { id: 'set_credit', name: 'Set Credit for user', path: '/SetCredit', icon: <CreditScoreOutlinedIcon fontSize="small" /> },
    { id: 'product_warehouse_management', name: 'Product Warehouse Management', path: '/ProductWarehouseManagement', icon: <WarehouseIcon fontSize="small" /> },
    { id: 'deal_of_the_day', name: 'Deal of the day', path: '/dealoftheday', icon: <PublicOutlinedIcon fontSize="small" /> },
    { id: 'coupon_management', name: 'Coupon Management', path: '/couponManagement', icon: <AutoAwesomeIcon fontSize="small" /> },
    { id: 'admin_coupon_users', name: 'Coupon Admin Management', path: '/AdminCouponUsers', icon: <AutoFixHighIcon fontSize="small" /> },
    { id: 'reward_management', name: 'Reward Management', path: '/rewardManagement', icon: <StarsIcon fontSize="small" /> },
    { id: 'reward_settings_management', name: 'Reward Settings Management', path: '/RewardSettingsManagement', icon: <MilitaryTechIcon fontSize="small" /> },
    { id: 'admin_rewards', name: 'Admin Reward Management', path: '/AdminRewards', icon: <ManageAccountsIcon fontSize="small" /> },
    { id: 'analytics', name: 'Report & Analytics', path: '/analytics', icon: <GavelIcon fontSize="small" /> },
    { id: 'flash_sale', name: 'Flash Sale', path: '/flashSale', icon: <GroupIcon fontSize="small" /> },
    { id: 'delivery_management', name: 'Delivery Management', path: '/deliveryManagement', icon: <CreditScoreOutlinedIcon fontSize="small" /> },
    { id: 'shipping_management', name: 'Shipping Management', path: '/shippingManagement', icon: <CreditScoreOutlinedIcon fontSize="small" /> },
    { id: 'delivery_settings', name: 'Delivery Settings', path: '/deliverySettings', icon: <AirportShuttleIcon fontSize="small" /> },
  ];

  // Check if user has access to a menu
  const hasAccess = (menuId: string) => {
    // If custom permissions exist, use them
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.hasAccess || false;
    }
    
    // Default permissions based on rolev
    if (isSuperAdmin) return true;
    if (isAdmin) return true; // Admin has access to all regular menus
    if (isOperation) {
      // Operation only has access to: order Management, Product Management
      return ['order_management', 'product_management'].includes(menuId);
    }
    return false;
  };

  // Check if user can write to a menu
  const hasWrite = (menuId: string) => {
    // If custom permissions exist, use them
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.canWrite || false;
    }
    
    // Default permissions based on role
    if (isSuperAdmin) return true;
    if (isAdmin) return true; // Admin has write access to all regular menus
    if (isOperation) {
      // Operation: Full write access to Order Management and Product Management
      return ['order_management', 'product_management'].includes(menuId);
    }
    return false;
  };

  // Check Admin Users section access
  const hasAdminUsersAccess = () => {
    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions['role_master']?.hasAccess || 
             adminPermissions['role_mapping']?.hasAccess || 
             adminPermissions['register_admin']?.hasAccess;
    }
    
    // Default: Only super admin has access to Admin Users
    return isSuperAdmin;
  };

  const hasAdminMenuAccess = (menuId: string) => {
    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.hasAccess || false;
    }
    
    // Default: Only super admin
    return isSuperAdmin;
  };

  const hasAdminMenuWrite = (menuId: string) => {
    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.canWrite || false;
    }
    
    // Default: Only super admin
    return isSuperAdmin;
  };

  const menuStyle = {
    py: 1.25,
    px: 2,
    borderRadius: '8px',
    mx: 1,
    mb: 0.5,
    color: '#303030',
    '&:hover': { backgroundColor: '#f1f1f1' },
    '&.Mui-selected': {
      backgroundColor: '#dfe3e8',
      color: '#008060',
      fontWeight: 600,
      '& .MuiListItemIcon-root': { color: '#008060' },
      '& .MuiListItemText-primary': { fontWeight: 600 },
    },
  };

  const lockedStyle = {
    ...menuStyle,
    opacity: 0.4,
    cursor: 'not-allowed',
    '&:hover': { backgroundColor: 'transparent' },
  };

  const MenuItem = ({ item }: { item: any }) => {
    const isActive = location.pathname === item.path;
    const access = hasAccess(item.id);
    const write = hasWrite(item.id);

    if (!access) {
      return (
        <Tooltip title="No access - Contact Super Admin" placement="right">
          <Box>
            <ListItemButton sx={lockedStyle} disabled>
              <ListItemIcon sx={{ minWidth: 40, opacity: 0.4 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
              <LockIcon fontSize="small" sx={{ color: '#dc2626', fontSize: '16px' }} />
            </ListItemButton>
          </Box>
        </Tooltip>
      );
    }

    return (
      <ListItemButton component={Link} to={item.path} sx={menuStyle} selected={isActive}>
        <ListItemIcon sx={{ minWidth: 40, color: isActive ? '#008060' : '#5c5f62' }}>{item.icon}</ListItemIcon>
        <ListItemText 
          primary={item.name} 
          primaryTypographyProps={{ 
            fontSize: '14px', 
            fontWeight: isActive ? 600 : 500,
            color: isActive ? '#008060' : '#303030'
          }} 
        />
        {!write && (
          <Tooltip title="Read-only access" placement="right">
            <LockIcon fontSize="small" sx={{ color: '#ffa500', fontSize: '16px' }} />
          </Tooltip>
        )}
      </ListItemButton>
    );
  };

  return (
    <Box sx={{ 
      width: 250, 
      height: '100vh', 
      backgroundColor: '#f7f7f7', 
      borderRight: '1px solid #e1e3e5', 
      display: 'flex', 
      flexDirection: 'column', 
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-thumb': { backgroundColor: '#c4cdd5', borderRadius: '3px' }
    }}>
      {/* Header */}
      <Box sx={{ 
        px: 2, 
        py: 1.5, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        borderBottom: '1px solid #e1e3e5', 
        backgroundColor: '#fff' 
      }}>
        <img src={logo} alt="Logo" style={{ maxWidth: '70px', height: 'auto' }} />
        <IconButton onClick={onToggle} sx={{ display: { md: 'none' }, color: '#202223' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Role Badge */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Chip 
          label={userRole.replace('_', ' ').toUpperCase()} 
          size="small" 
          sx={{ 
            width: '100%',
            backgroundColor: isSuperAdmin ? '#ff9800' : isAdmin ? '#2196f3' : '#4caf50',
            color: 'white',
            fontWeight: 600,
            fontSize: '12px',
            height: '28px'
          }} 
        />
      </Box>

      {/* Menu Items */}
      <List sx={{ pt: 2, pb: 2 }}>
        {getMenuItems().map(item => <MenuItem key={item.id} item={item} />)}

        {/* Admin Users Dropdown */}
        <Box>
          <ListItemButton 
            onClick={() => hasAdminUsersAccess() && setShowAdminUsersMenu(!showAdminUsersMenu)} 
            sx={hasAdminUsersAccess() ? menuStyle : lockedStyle}
            disabled={!hasAdminUsersAccess()}
          >
            <ListItemIcon sx={{ minWidth: 40, opacity: hasAdminUsersAccess() ? 1 : 0.4 }}>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Admin Users" primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
            {!hasAdminUsersAccess() && <LockIcon fontSize="small" sx={{ color: '#dc2626', fontSize: '16px', mr: 1 }} />}
            {hasAdminUsersAccess() && (showAdminUsersMenu ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
          </ListItemButton>
          
          {hasAdminUsersAccess() && (
            <Collapse in={showAdminUsersMenu}>
              <List disablePadding>
                {/* Role Master */}
                {hasAdminMenuAccess('role_master') ? (
                  <ListItemButton component={Link} to="/roleMaster" sx={{ ...menuStyle, pl: 6, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}><SecurityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Role Master" primaryTypographyProps={{ fontSize: '13px', fontWeight: 400 }} />
                    {!hasAdminMenuWrite('role_master') && (
                      <Tooltip title="Read-only"><LockIcon fontSize="small" sx={{ color: '#ffa500', fontSize: '14px' }} /></Tooltip>
                    )}
                  </ListItemButton>
                ) : (
                  <Tooltip title="No access"><Box><ListItemButton sx={{ ...lockedStyle, pl: 6, py: 1 }} disabled>
                    <ListItemIcon sx={{ minWidth: 30, opacity: 0.4 }}><SecurityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Role Master" primaryTypographyProps={{ fontSize: '13px' }} />
                    <LockIcon fontSize="small" sx={{ color: '#dc2626', fontSize: '14px' }} />
                  </ListItemButton></Box></Tooltip>
                )}

                {/* Role Mapping */}
                {hasAdminMenuAccess('role_mapping') ? (
                  <ListItemButton component={Link} to="/roleMapping" sx={{ ...menuStyle, pl: 6, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}><AssignmentIndIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Role Mapping" primaryTypographyProps={{ fontSize: '13px', fontWeight: 400 }} />
                    {!hasAdminMenuWrite('role_mapping') && (
                      <Tooltip title="Read-only"><LockIcon fontSize="small" sx={{ color: '#ffa500', fontSize: '14px' }} /></Tooltip>
                    )}
                  </ListItemButton>
                ) : (
                  <Tooltip title="No access"><Box><ListItemButton sx={{ ...lockedStyle, pl: 6, py: 1 }} disabled>
                    <ListItemIcon sx={{ minWidth: 30, opacity: 0.4 }}><AssignmentIndIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Role Mapping" primaryTypographyProps={{ fontSize: '13px' }} />
                    <LockIcon fontSize="small" sx={{ color: '#dc2626', fontSize: '14px' }} />
                  </ListItemButton></Box></Tooltip>
                )}

                {/* Manage Admin User */}
                {hasAdminMenuAccess('register_admin') ? (
                  <ListItemButton component={Link} to="/RegisterAdmin" sx={{ ...menuStyle, pl: 6, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}><PersonAddIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Manage Admin User" primaryTypographyProps={{ fontSize: '13px', fontWeight: 400 }} />
                    {!hasAdminMenuWrite('register_admin') && (
                      <Tooltip title="Read-only"><LockIcon fontSize="small" sx={{ color: '#ffa500', fontSize: '14px' }} /></Tooltip>
                    )}
                  </ListItemButton>
                ) : (
                  <Tooltip title="No access"><Box><ListItemButton sx={{ ...lockedStyle, pl: 6, py: 1 }} disabled>
                    <ListItemIcon sx={{ minWidth: 30, opacity: 0.4 }}><PersonAddIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Manage Admin User" primaryTypographyProps={{ fontSize: '13px' }} />
                    <LockIcon fontSize="small" sx={{ color: '#dc2626', fontSize: '14px' }} />
                  </ListItemButton></Box></Tooltip>
                )}
              </List>
            </Collapse>
          )}
        </Box>

        {/* Settings Dropdown */}
        {hasAccess('settings') && (
          <Box>
            <ListItemButton onClick={() => setShowSettingsMenu(!showSettingsMenu)} sx={menuStyle}>
              <ListItemIcon sx={{ minWidth: 40 }}><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
              {showSettingsMenu ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </ListItemButton>
            <Collapse in={showSettingsMenu}>
              <List disablePadding>
                <ListItemButton component={Link} to="/smptsettings" sx={{ ...menuStyle, pl: 6, py: 1 }}>
                  <ListItemText primary="SMTP Settings" primaryTypographyProps={{ fontSize: '13px', fontWeight: 400 }} />
                </ListItemButton>
                <ListItemButton component={Link} to="/globalSettings" sx={{ ...menuStyle, pl: 6, py: 1 }}>
                  <ListItemText primary="Global Settings" primaryTypographyProps={{ fontSize: '13px', fontWeight: 400 }} />
                </ListItemButton>
                <ListItemButton component={Link} to="/smsSettings" sx={{ ...menuStyle, pl: 6, py: 1 }}>
                  <ListItemText primary="SMS Settings" primaryTypographyProps={{ fontSize: '13px', fontWeight: 400 }} />
                </ListItemButton>
              </List>
            </Collapse>
          </Box>
        )}
      </List>
    </Box>
  );
};

export default SlideMenu;